import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonTitle,
  IonToggle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { chevronDownOutline, chevronForwardOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";

import Header from "src/components/Header";
import {
  MODULE_DEFINITIONS,
  MODULE_PERMISSIONS_ADMIN_CEDULA,
  ModuleDefinition,
  ModulePermissionsMap,
  expandPermissionsWithCargos,
  getMergedModulePermissions,
  loadModulePermissions,
  normalizeCargoKey,
  saveModulePermissions,
} from "src/config/modulePermissions";
import LoginAPI from "src/services/login";
import { obtenerCargosOrganizacion } from "src/services/cargosOrganizacion";
import { actualizarPermisosModulo, obtenerPermisosModulos } from "src/services/modulePermissions";

const groupLabelForCargo = (cargo: string) => {
  const upper = String(cargo || "").toUpperCase();

  if (upper.includes("DESARROLLADOR")) return "DESARROLLADOR";
  if (upper.includes("FISCAL")) return "FISCAL";
  if (upper.includes("SUPERVISOR")) return "SUPERVISOR";
  if (upper.includes("OPERARIO") || upper.includes("OPERARIA")) return "OPERARIO";
  if (upper.includes("JEFE")) return "JEFE";
  if (upper.includes("GERENTE")) return "GERENTE";
  if (upper.includes("LIDER") || upper.includes("LÍDER")) return "LIDER";
  if (upper.includes("COORDINADOR") || upper.includes("COORD")) return "COORDINADOR";
  if (upper.includes("ANALISTA")) return "ANALISTA";
  if (upper.includes("AUXILIAR")) return "AUXILIAR";
  if (upper.includes("CAPACITADOR")) return "CAPACITADOR";
  if (upper.includes("TECNIC") || upper.includes("TÉCNIC")) return "TECNICO";
  if (upper.includes("TIC") || upper.includes("INFORMATICA") || upper.includes("TECNOLOGIA")) return "TECNOLOGIA";

  const firstWord = upper.split(/\s+/).filter(Boolean)[0] || "OTROS";
  return firstWord;
};

const sortGroups = (groups: string[]) => {
  const priority = [
    "DESARROLLADOR",
    "FISCAL",
    "SUPERVISOR",
    "OPERARIO",
    "JEFE",
    "GERENTE",
    "LIDER",
    "COORDINADOR",
    "ANALISTA",
    "AUXILIAR",
    "CAPACITADOR",
    "TECNICO",
    "TECNOLOGIA",
    "OTROS",
  ];

  return [...groups].sort((a, b) => {
    const ai = priority.indexOf(a);
    const bi = priority.indexOf(b);
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0) return -1;
    if (bi >= 0) return 1;
    return a.localeCompare(b, "es");
  });
};

const ModulePermissions: React.FC = () => {
  const [present] = useIonToast();
  const [loading, setLoading] = useState(true);
  const [autorizado, setAutorizado] = useState(false);
  const [permissions, setPermissions] = useState<ModulePermissionsMap>(() => loadModulePermissions());
  const [selectedModule, setSelectedModule] = useState<ModuleDefinition | null>(null);
  const [draft, setDraft] = useState<ModulePermissionsMap>(() => loadModulePermissions());
  const [cargos, setCargos] = useState<string[]>([]);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const response = await LoginAPI.me();
        const perfil = response?.data?.datos || {};
        const cedula = String(perfil?.emp_cedula || "").trim();
        setAutorizado(cedula === MODULE_PERMISSIONS_ADMIN_CEDULA);
      } catch {
        setAutorizado(false);
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    const cargarCargos = async () => {
      try {
        const [data, remotos] = await Promise.all([
          obtenerCargosOrganizacion(),
          obtenerPermisosModulos(),
        ]);
        const lista = Array.from(
          new Set(
            (data || [])
              .map((item) => String(item?.cargo || "").trim())
              .filter(Boolean)
          )
        );

        const ordered = lista.sort((a, b) => a.localeCompare(b, "es"));
        const expanded = expandPermissionsWithCargos(remotos, ordered);
        setCargos(ordered);
        setPermissions(expanded);
        setDraft(expanded);
        saveModulePermissions(expanded);
      } catch {
        setCargos([]);
        setPermissions(getMergedModulePermissions(loadModulePermissions()));
        setDraft(getMergedModulePermissions(loadModulePermissions()));
      }
    };

    if (autorizado) {
      cargarCargos();
    }
  }, [autorizado]);

  const groupedCargos = useMemo(() => {
    const grouped: Record<string, string[]> = {};

    cargos.forEach((cargo) => {
      const group = groupLabelForCargo(cargo);
      grouped[group] = grouped[group] || [];
      grouped[group].push(cargo);
    });

    const orderedKeys = sortGroups(Object.keys(grouped));
    return orderedKeys.map((group) => ({
      group,
      cargos: (grouped[group] || []).sort((a, b) => a.localeCompare(b, "es")),
    }));
  }, [cargos]);

  const openModule = (module: ModuleDefinition) => {
    const current = expandPermissionsWithCargos(permissions, cargos);
    setDraft(current);
    setSelectedModule(module);

    const initialGroups: Record<string, boolean> = {};
    groupedCargos.forEach((item, index) => {
      initialGroups[item.group] = index < 2;
    });
    setOpenGroups(initialGroups);
  };

  const closeModal = () => {
    setSelectedModule(null);
  };

  const toggleGroup = (group: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const updateSwitch = (moduleId: string, cargoLabel: string, checked: boolean) => {
    setDraft((prev) => {
      const next = getMergedModulePermissions(prev);
      if (!next[moduleId]) return next;
      const key = normalizeCargoKey(cargoLabel);
      next[moduleId] = {
        ...next[moduleId],
        [key]: checked,
      };
      return next;
    });
  };

  const saveModule = async () => {
    if (!selectedModule) return;

    try {
      const merged = expandPermissionsWithCargos(draft, cargos);
      const moduleId = String(selectedModule.id || "").trim().toLowerCase();
      const modulePayload = merged[moduleId] || {};
      const guardados = await actualizarPermisosModulo(moduleId, modulePayload);
      const expanded = expandPermissionsWithCargos(guardados, cargos);
      setPermissions(expanded);
      saveModulePermissions(expanded);
      closeModal();
      present({
        message: `Permisos actualizados para ${selectedModule.title}.`,
        duration: 2200,
        color: "success",
      });
    } catch {
      present({
        message: "No se pudieron guardar los permisos en servidor.",
        duration: 2400,
        color: "danger",
      });
    }
  };

  const enabledCountByModule = useMemo(() => {
    return MODULE_DEFINITIONS.reduce((acc, module) => {
      const modulePerms = permissions[module.id] || {};
      const count = (cargos || []).filter((cargo) => Boolean(modulePerms[normalizeCargoKey(cargo)])).length;
      acc[module.id] = count;
      return acc;
    }, {} as Record<string, number>);
  }, [permissions, cargos]);

  if (loading) {
    return (
      <IonPage>
        <Header titulo="Permisos por Modulo" />
        <IonContent id="module-permissions">
          <div className="container mt-1">
            <div className="kpi-card">
              <div className="kpi-item-row">Cargando...</div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!autorizado) {
    return (
      <IonPage>
        <Header titulo="Permisos por Modulo" />
        <IonContent id="module-permissions">
          <div className="container mt-1">
            <div className="kpi-card">
              <div className="kpi-item-row">No autorizado.</div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo="Permisos por Modulo" />
      <IonContent id="module-permissions">
        <div className="container mt-1">
          <div className="kpi-card" style={{ marginBottom: 12 }}>
            <div className="kpi-title">Configuracion de acceso por modulo</div>
            <div className="kpi-item-row">
              Toca un modulo para abrir su configuracion y activar/desactivar cargos reales del DWH.
            </div>
          </div>

          <div className="kpi-grid" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {MODULE_DEFINITIONS.map((module) => (
              <button
                key={module.id}
                type="button"
                className="kpi-card kpi-clickable"
                style={{ textAlign: "left" }}
                onClick={() => openModule(module)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div className="kpi-title" style={{ marginBottom: 4 }}>{module.title}</div>
                  <IonBadge color={enabledCountByModule[module.id] > 0 ? "success" : "medium"}>
                    {enabledCountByModule[module.id] || 0} activos
                  </IonBadge>
                </div>
                <div className="kpi-item-row">{module.description}</div>
              </button>
            ))}
          </div>
        </div>

        <IonModal isOpen={Boolean(selectedModule)} onDidDismiss={closeModal} cssClass="kpi-modal">
          <IonHeader>
            <IonToolbar>
              <IonTitle>{selectedModule?.title || "Modulo"}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={closeModal}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent className="kpi-modal-body">
            {selectedModule && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div className="kpi-item-row">{selectedModule.description}</div>

                {groupedCargos.length === 0 ? (
                  <div className="kpi-item-row">No se pudieron cargar cargos desde DWH.</div>
                ) : (
                  groupedCargos.map((groupItem) => {
                    const enabledInGroup = groupItem.cargos.filter((cargo) =>
                      Boolean(draft[selectedModule.id]?.[normalizeCargoKey(cargo)])
                    ).length;

                    return (
                      <div key={`${selectedModule.id}-${groupItem.group}`} className="kpi-card" style={{ padding: 0 }}>
                        <IonItem button detail={false} onClick={() => toggleGroup(groupItem.group)}>
                          <IonLabel>
                            <div style={{ fontWeight: 600 }}>{groupItem.group}</div>
                            <div className="kpi-item-row">
                              {enabledInGroup}/{groupItem.cargos.length} activos
                            </div>
                          </IonLabel>
                          <IonIcon
                            slot="end"
                            icon={openGroups[groupItem.group] ? chevronDownOutline : chevronForwardOutline}
                            color="medium"
                          />
                        </IonItem>

                        {openGroups[groupItem.group] && (
                          <IonList>
                            {groupItem.cargos.map((cargoLabel) => (
                              <IonItem key={`${selectedModule.id}-${groupItem.group}-${cargoLabel}`}>
                                <IonLabel>{cargoLabel}</IonLabel>
                                <IonToggle
                                  slot="end"
                                  checked={Boolean(draft[selectedModule.id]?.[normalizeCargoKey(cargoLabel)])}
                                  onIonChange={(e) =>
                                    updateSwitch(selectedModule.id, cargoLabel, Boolean(e.detail.checked))
                                  }
                                />
                              </IonItem>
                            ))}
                          </IonList>
                        )}
                      </div>
                    );
                  })
                )}

                <div className="kpi-gestion-actions">
                  <IonButton fill="outline" onClick={closeModal}>Cancelar</IonButton>
                  <IonButton onClick={saveModule}>Guardar cambios</IonButton>
                </div>
              </div>
            )}
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default ModulePermissions;
